import { useCallback, useRef } from 'react'

const DRAG_THRESHOLD = 4 // px before a press counts as a drag

const useDragScroll = () => {
  // Holds the teardown for the currently-attached node so we can detach on unmount.
  const cleanupRef = useRef(null)

  return useCallback((el) => {
    // Node detached (or replaced): tear down previous listeners first.
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }

    if (!el) return

    let isDown = false
    let startX = 0
    let startScroll = 0
    let moved = false

    const onMouseDown = (e) => {
      isDown = true
      moved = false
      startX = e.pageX - el.offsetLeft
      startScroll = el.scrollLeft
      el.classList.add('dragging')
      e.preventDefault() // avoid native image/text drag ghost
    }

    const onMouseMove = (e) => {
      if (!isDown) return
      const walk = e.pageX - el.offsetLeft - startX
      if (Math.abs(walk) > DRAG_THRESHOLD) moved = true
      el.scrollLeft = startScroll - walk
    }

    const stop = () => {
      isDown = false
      el.classList.remove('dragging')
    }

    // Capture phase: cancel the post-drag click before it reaches the cards.
    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault()
        e.stopPropagation()
        moved = false
      }
    }

    el.addEventListener('mousedown', onMouseDown)
    el.addEventListener('mousemove', onMouseMove)
    el.addEventListener('mouseleave', stop)
    window.addEventListener('mouseup', stop)
    el.addEventListener('click', onClickCapture, true)

    cleanupRef.current = () => {
      el.removeEventListener('mousedown', onMouseDown)
      el.removeEventListener('mousemove', onMouseMove)
      el.removeEventListener('mouseleave', stop)
      window.removeEventListener('mouseup', stop)
      el.removeEventListener('click', onClickCapture, true)
    }
  }, [])
}

export default useDragScroll
